import React, { useState, useEffect, useContext } from "react";
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from "react-router-dom";
import PopularPackageLinks from "./PopularPackageLinks";
import PackagesCards from "./PackagesCards";
import { ThemeContext } from "../context/ThemeContext";

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
};

const AboutUs = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const handleNavigation = () => {
    navigate("/enquiry-page");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="mt-6 py-32 bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950 relative overflow-hidden text-gray-800 dark:text-gray-100">
      <div className="container mx-auto px-4 max-w-6xl relative">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="space-y-16"
        >
          <motion.div
            variants={cardVariants}
            className="text-center mb-20"
          >
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent mb-6">
              About Explore & Travel
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto font-medium">
              Crafting Unforgettable Journeys Across the Globe
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Destinations Card */}
            <motion.div
              variants={cardVariants}
              whileHover={{ y: -10 }}
              className="group relative h-full"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-400/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
              <div className="relative bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-gray-700 group-hover:border-transparent transition-all h-full">
                <div className="absolute top-6 right-6 w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center">
                  ✈️
                </div>
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">Our Destinations</h2>
                <ul className="space-y-4">
                  {[
                    "Dubai's architectural wonders",
                    "Malaysian cultural treasures",
                    "India's natural beauty",
                    "Global adventure spots"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start text-gray-700 dark:text-gray-300 pl-4 relative">
                      <div className="absolute left-0 top-3 w-2 h-2 bg-blue-500 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Services Card */}
            <motion.div
              variants={cardVariants}
              whileHover={{ y: -10 }}
              className="group relative h-full"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-400/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
              <div className="relative bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-gray-700 group-hover:border-transparent transition-all h-full">
                <div className="absolute top-6 right-6 w-12 h-12 bg-cyan-100 dark:bg-cyan-900 rounded-xl flex items-center justify-center">
                  🏨
                </div>
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">Our Services</h2>
                <ul className="space-y-4">
                  {[
                    "Tailored travel packages",
                    "Luxurious international getaways",
                    "Immersive local tours",
                    "Customizable itineraries"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start text-gray-700 dark:text-gray-300 pl-4 relative">
                      <div className="absolute left-0 top-3 w-2 h-2 bg-cyan-500 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Why Choose Us Card */}
            <motion.div
              variants={cardVariants}
              whileHover={{ y: -10 }}
              className="group relative h-full"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-cyan-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
              <div className="relative bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-gray-700 group-hover:border-transparent transition-all h-full">
                <div className="absolute top-6 right-6 w-12 h-12 bg-blue-50 dark:bg-blue-900 rounded-xl flex items-center justify-center">
                  ⭐
                </div>
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">Why Choose Us?</h2>
                <ul className="space-y-4">
                  {[
                    "Expert-curated experiences",
                    "24/7 customer support",
                    "Premium quality value",
                    "Safe & secure travel",
                    "Exclusive deals"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start text-gray-700 dark:text-gray-300 pl-4 relative">
                      <div className="absolute left-0 top-3 w-2 h-2 bg-blue-600 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Mission Card */}
            <motion.div
              variants={cardVariants}
              whileHover={{ y: -10 }}
              className="group relative h-full"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/10 to-blue-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
              <div className="relative bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-gray-700 group-hover:border-transparent transition-all h-full">
                <div className="absolute top-6 right-6 w-12 h-12 bg-cyan-50 dark:bg-cyan-900 rounded-xl flex items-center justify-center">
                  🌍
                </div>
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">Our Mission</h2>
                <ul className="space-y-4">
                  {[
                    "Transform travel dreams",
                    "Create lasting memories",
                    "Inspire global exploration"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start text-gray-700 dark:text-gray-300 pl-4 relative">
                      <div className="absolute left-0 top-3 w-2 h-2 bg-cyan-600 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>

          <motion.div
            variants={cardVariants}
            className="text-center mt-20"
          >
            <div className="inline-block relative group">
              <button
                onClick={handleNavigation}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-10 py-4 rounded-2xl font-semibold hover:shadow-xl transition-all relative overflow-hidden z-10"
              >
                <span
                  className="relative z-10 cursor-pointer hover:text-cyan-400 transition-colors duration-200"
                >
                  Begin Your Adventure
                  <span className="ml-3">→</span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 mix-blend-overlay" />
              </button>
              <div className="absolute -inset-2 bg-gradient-to-r from-blue-600/20 to-cyan-500/20 blur-lg rounded-2xl group-hover:opacity-50 transition-opacity" />
            </div>
          </motion.div>
        </motion.div>
      </div>
      <PackagesCards />
      <PopularPackageLinks />
    </section>
  );
};

export default AboutUs;
